import React, {useState} from "react";
import {Tag, Select, message} from "antd";
import api from "../api";

export interface HotelRecord {
    id: number;
    status: "before_confirmation" | "confirmed" | "change_requested" | "confirmed_with_changes" | "cancelled";
    customer: {
        id: number;
        first_name: string;
        last_name: string;
        mmid: string;
    };
    checkin_date?: string;
    checkout_date?: string;
    rooms?: string;
    tower?: string;
    room_types?: string[];
    special_requests?: string[];
    early_checkin_time?: string | null;
    late_checkout_time?: string | null;
    splash_bay?: boolean | string;
    payment_type?: string;
    confirmation_number?: string;
    notes?: string;

    [key: string]: any;
}

interface EditableStatusCellProps {
    record: HotelRecord;
    fetchHotels: (page?: number, pageSize?: number) => Promise<void>;
    pagination: { current: number; pageSize: number };
}

const statusColors: Record<string, string> = {
    before_confirmation: "orange",
    confirmed: "green",
    change_requested: "volcano",
    confirmed_with_changes: "blue",
    cancelled: "red",
};

const statusLabels: Record<string, string> = {
    before_confirmation: "Before Confirmation",
    confirmed: "Confirmed",
    change_requested: "Change Requested",
    confirmed_with_changes: "Confirmed (with changes)",
    cancelled: "Cancelled",
};

const EditableStatusCell: React.FC<EditableStatusCellProps> = ({
                                                                   record,
                                                                   fetchHotels,
                                                                   pagination,
                                                               }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState<string>(record.status);

    const handleChange = async (newStatus: string) => {
        try {
            await api.patch(`/cc/hotel/${record.id}/`, {status: newStatus});
            message.success("Status updated");
            setValue(newStatus);
            setEditing(false);
            fetchHotels(pagination.current, pagination.pageSize);
        } catch {
            message.error("Failed to update status");
        }
    };

    // 🚫 Filter options: hide "before_confirmation" if confirmation exists
    const allowedStatuses = Object.keys(statusLabels).filter((statusKey) =>
        record.confirmation_number ? statusKey !== "before_confirmation" : true
    );

    if (!editing) {
        return (
            <Tag
                color={statusColors[value]}
                onClick={(e) => {
                    e.stopPropagation();
                    setEditing(true);
                }}
                style={{cursor: "pointer"}}
            >
                {statusLabels[value]}
            </Tag>
        );
    }

    return (
        <Select
            value={value}
            onClick={(e) => e.stopPropagation()}
            onChange={handleChange}
            onBlur={() => setEditing(false)}
            style={{width: 220}}
            autoFocus
        >
            {allowedStatuses.map((statusKey) => (
                <Select.Option key={statusKey} value={statusKey}>
                    <Tag color={statusColors[statusKey]} style={{marginRight: 8}}>
                        {statusLabels[statusKey]}
                    </Tag>
                    {statusLabels[statusKey]}
                </Select.Option>
            ))}
        </Select>
    );
};

export default EditableStatusCell;